import Axios from 'axios';
import { openReport } from '../../../utils/sessionGuard'; // セッション失効時は新タブを開かず現在の画面でログインへ
import { collectMyTeamMemberIds } from '../../../utils/teamMemberIds'; // 360度チームメンバーIDの収集（グループ全ユーザ取得の廃止）
// 簡易ログイン（メール未確認）セッションでは結果のダウンロードを行わせない
import {
  isTrialQuickSession,
  blockResultForQuickSession,
} from '../../../utils/trialGuard';
import React, { Fragment, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button, Spin, message, Space, Modal } from 'antd';
import config from '../../Config/key';
// 共用コンポーネント（デザインシステム Ph.3）
// StatusBadge / SURVEY_STATUS_TONE は MySurvey と同じステータス表現で統一する。
import {
  PageContainer,
  EmptyState,
  StatusBadge,
  SURVEY_STATUS_TONE,
} from '../../common';
import { surveyCode } from '../../Config/surveyCode';
import { surveyStatus } from '../../Config/surveyStatus';
import { BsBoxArrowUpRight } from 'react-icons/bs';
import { RightOutlined, WarningOutlined } from '@ant-design/icons';
import './LandingPage.css';

const { confirm } = Modal;
// MySurveyPage と同じく、チーム集計用に全ユーザー・選択シーズン ID をモジュールスコープで保持する。
let tUsers = [];
let selSurveySeasonId = '';

/**
 * LandingPage（Home / ダッシュボード）
 * ------------------------------------------------------------------
 * 目標デザイン（home_デザイン版）準拠の「① 回答する / ② 結果を確認する」カード
 * ダッシュボード。表示データ・分類・遷移はすべて MySurveyPage に揃える
 * （データ取得・トークン加工・回答開始/結果確認のハンドラを MySurvey から移植）。
 * 「運営からのお知らせ」セクションは今回スコープ外（別途）。
 */
function LandingPage(props) {
  const { user } = props;
  const dispatch = useDispatch(); // ログアウト処理（簡易ログインセッションの結果ブロック時）で使用
  const [mySurvey, setMySurvey] = useState([]);
  const [selSeasonInfo, setSelSeasonInfo] = useState([]);
  const [groupInfo, setGroupInfo] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  // サーベイ一覧の読み込み中フラグ。大規模グループではグループ全体のユーザ取得に
  // 時間がかかり、空のまま表示すると「配信されたサーベイが無い」と誤認されるため、
  // 読み込み完了までスピナーを表示し、完了後にのみ空状態メッセージを出す。
  const [isLoading, setIsLoading] = useState(true);

  const isAuth = !!(user && user.userData && user.userData.isAuth);
  // トライアルユーザー判定（trial_campaign_key を持つユーザー）
  const isTrialUser = !!(
    user &&
    user.userData &&
    user.userData.trialCampaignKey
  );
  // ログインユーザーの氏名（姓 名）。セクション説明文に差し込む。
  const userName =
    user && user.userData
      ? `${user.userData.lastname || ''} ${user.userData.name || ''}`.trim()
      : '';

  //yyyy/mm/dd
  const ymd = (tdate) => {
    let date = new Date(tdate);
    return (
      date.getFullYear() +
      '/' +
      ('0' + (date.getMonth() + 1)).slice(-2) +
      '/' +
      ('0' + date.getDate()).slice(-2)
    );
  };
  //yyyy.mm.dd（お知らせの日付表示用）
  const ymdDot = (tdate) => {
    let date = new Date(tdate);
    return (
      date.getFullYear() +
      '.' +
      ('0' + (date.getMonth() + 1)).slice(-2) +
      '.' +
      ('0' + date.getDate()).slice(-2)
    );
  };
  const isPassed = (targetDate) => {
    const now = new Date();
    // 日付のみで比較（時刻を無視してJST 0時から公開されるようにする）
    return ymd(targetDate) <= ymd(now);
  };

  // サーベイ日程表示用（日付のみ）
  const formatSurveyDateOnly = (dateValue) => {
    if (!dateValue) return '-';
    // 既にフォーマット済みの場合
    if (typeof dateValue === 'string' && dateValue.includes('/')) {
      return dateValue.split(' ')[0]; // 時間部分削除
    }
    // ISO文字列から日付部分のみ抽出
    const dateStr = dateValue.split('T')[0];
    const [year, month, day] = dateStr.split('-');
    return `${year}/${month}/${day}`;
  };

  // UTC instant（Date|ISO）→ JST(UTC+9) の暦日 'YYYY/MM/DD'（表示用）
  // token_expire は「登録時刻 + N日」の実 timestamp のため、ブラウザの
  // ローカルTZに依存せず日本基準の日付を出すよう getTime()+9h → getUTC* で算出。
  const formatJstDateOnly = (dateValue) => {
    if (!dateValue) return '-';
    const jst = new Date(new Date(dateValue).getTime() + 9 * 60 * 60 * 1000);
    return (
      jst.getUTCFullYear() +
      '/' +
      ('0' + (jst.getUTCMonth() + 1)).slice(-2) +
      '/' +
      ('0' + jst.getUTCDate()).slice(-2)
    );
  };

  // UTC instant → JST 'YYYY-MM-DD'（大小比較用キー）
  const jstDateKey = (dateValue) => {
    const jst = new Date(new Date(dateValue).getTime() + 9 * 60 * 60 * 1000);
    return (
      jst.getUTCFullYear() +
      '-' +
      ('0' + (jst.getUTCMonth() + 1)).slice(-2) +
      '-' +
      ('0' + jst.getUTCDate()).slice(-2)
    );
  };

  // サーベイ回答日程の範囲内判定（日付のみで比較、両端を含む）
  const isInSurveyPeriod = (startDateStr, endDateStr) => {
    if (!startDateStr || !endDateStr) return false;
    const start = startDateStr.split('T')[0];
    const end = endDateStr.split('T')[0];
    const today = new Date();
    const todayStr =
      today.getFullYear() +
      '-' +
      ('0' + (today.getMonth() + 1)).slice(-2) +
      '-' +
      ('0' + today.getDate()).slice(-2);
    // 開始日当日から終了日当日まで有効
    return todayStr >= start && todayStr <= end;
  };

  const readDB = async () => {
    try {
      if (!user.userData) return null;
      if (user.userData.isAuth) {
        const [surveyInfo, seasonInfo, tokenInfo, group] = await Promise.all([
          Axios.get('/api/survey/'),
          Axios.get(`/api/surveySeason/g/${user.userData.groupCode}`),
          Axios.get(`api/surveyToken/u/${user.userData._id}`),
          Axios.get(`/api/group/${user.userData.groupCode}`),
        ]);

        // 360度チーム進捗計算に必要なチームメンバーのみ取得
        // （旧: グループ全ユーザ取得。数千名グループでホーム表示遅延の主因だった。
        //   360度系サーベイが無いユーザーは対象0件のため照会自体を省略する）
        const teamIds = collectMyTeamMemberIds(
          [
            seasonInfo.data.surveySeason,
            group.data.group && group.data.group.survey_season,
          ],
          user.userData._id
        );
        if (teamIds.length > 0) {
          const teamRes = await Axios.post(
            `${config.USER_SERVER}/team-members`,
            { userIds: teamIds }
          );
          tUsers = teamRes.data.users;
        } else {
          tUsers = [];
        }

        let surveyToken = tokenInfo.data.token.concat();
        let sortedToken = tokenInfo.data.token.concat().sort(function (a, b) {
          return a.updatedAt > b.updatedAt ? -1 : 1; //オブジェクトの昇順ソート
        });
        let surveySeason = seasonInfo.data.surveySeason;
        let survey = surveyInfo.data.survey;
        setGroupInfo(group.data.group);
        const gInfo = group.data.group;

        if (!surveyInfo.data.survey)
          throw new Error('サーベイ取得に失敗しました。');
        if (!surveyToken) throw new Error('トークン取得に失敗しました。');

        // 一時保存データの存在確認
        const tokenIds = surveyToken.map((token) => token._id);
        let tempAnswerStatus = {};
        try {
          const tempCheckResponse = await Axios.post(
            '/api/tempAnswer/checkMultiple',
            { tokenIds }
          );
          if (tempCheckResponse.data.success) {
            tempAnswerStatus = tempCheckResponse.data.tempAnswerStatus;
          }
        } catch (error) {
          console.log('一時保存データ確認エラー:', error);
        }

        await surveyToken.map(async (token) => {
          token.surveyName = surveyCode[token.survey_code];
          // 一時保存データがある場合は「一時保存中」に変更
          if (token.survey_status === 0 && tempAnswerStatus[token._id]) {
            token.survey_status = 1;
          }
          token.surveyStatusName = surveyStatus[token.survey_status];

          let surveyName = '';
          let tName = '';
          let relationInfo = [];
          //シーズンIDが存在するならシーズン名を表示
          if (token.survey_season_id) {
            selSurveySeasonId = token.survey_season_id;
            let surveySeasonInfo = surveySeason.find(
              (item) => item._id === token.survey_season_id
            );
            token.active = surveySeasonInfo.active;
            surveyName = surveySeasonInfo.season_name;
            tName = surveySeasonInfo.t_name;
            token.training_start_date = surveySeasonInfo.training_start_date;
            token.training_end_date = surveySeasonInfo.training_end_date;
            token.survey_start_date = surveySeasonInfo.survey_start_date;
            token.survey_end_date = surveySeasonInfo.survey_end_date;
            setSelSeasonInfo(surveySeasonInfo);

            if (token.survey_code === '40010') {
              if (token.relation === 1) {
                relationInfo = surveySeasonInfo.relation_360.find(
                  (item) => item.user_id === user.userData._id
                );
                token.relationInfo = relationInfo;
                token.output_360_type = surveySeasonInfo.output_360_type;
                token.output_360_date = surveySeasonInfo.output_360_date;
                token.type_360 = surveySeasonInfo.question_set[0].type_360;
                token.test = 0;
                let data = makeTeam(relationInfo);
                data.cnt += 1; //本人分の追加
                data.done = data.done + (token.survey_status === 5 ? 1 : 0);
                token.allStatus = data.cnt === data.done ? 1 : 0;
                let lastSeasonTokenInfo = sortedToken.find(
                  (tokenInfo) =>
                    tokenInfo.survey_status === 5 &&
                    tokenInfo.relation === 1 &&
                    tokenInfo.survey_code === '40010' &&
                    tokenInfo.survey_season_id != token.survey_season_id &&
                    ymd(tokenInfo.updatedAt) < ymd(token.updatedAt) &&
                    tokenInfo.question_set_id === token.question_set_id
                );
                let lastSeason = false;
                if (lastSeasonTokenInfo) {
                  const lastSurveySeason = gInfo.survey_season.find(
                    (item) => item._id === lastSeasonTokenInfo.survey_season_id
                  );
                  lastSeason = isLastSeasonComplete(
                    user.userData,
                    lastSurveySeason
                  );
                }
                token.lastSeason = lastSeason;
                if (lastSeason) {
                  token.lastSeasonTokenInfo = lastSeasonTokenInfo;
                  token.lastSurveySeason = gInfo.survey_season.find(
                    (item) => item._id === lastSeasonTokenInfo.survey_season_id
                  );
                }
              }
            } else if (
              token.survey_code === '90101' ||
              token.survey_code === '90502' ||
              token.survey_code === '90501'
            ) {
              token.coursePartView = surveySeasonInfo.course_part_view;
            } else if (token.survey_code === '90301') {
              token.coursePartView = surveySeasonInfo.course_part_view;
              token.e3_option = surveySeasonInfo.e3_option;
            } else if (token.survey_code === '30010') {
              token.e3_option = surveySeasonInfo.e3_option;
              if (token.e3_option === 1 || token.e3_option === 2) {
                token.output_360_type = surveySeasonInfo.output_360_type ?? 1;
                token.output_360_date = surveySeasonInfo.output_360_date;
              }
            } else if (token.survey_code === '60020') {
              token.type_360 = surveySeasonInfo.question_set[0].type_360;
              if (token.relation === 1) {
                relationInfo = surveySeasonInfo.relation_360.find(
                  (item) => item.user_id === user.userData._id
                );
                token.relationInfo = relationInfo;
                token.output_360_type = surveySeasonInfo.output_360_type;
                token.output_360_date = surveySeasonInfo.output_360_date;
                token.test = 0;
                let data = makeTeam(relationInfo);
                data.cnt += 1; //本人分の追加
                data.done = data.done + (token.survey_status === 5 ? 1 : 0);
                token.allStatus = data.cnt === data.done ? 1 : 0;
              }
            } else if (
              token.survey_code === '50020' ||
              token.survey_code === '50030' ||
              token.survey_code === '50040' ||
              token.survey_code === '10010' ||
              token.survey_code === '50010'
            ) {
              token.output_360_type = surveySeasonInfo.output_360_type ?? 1;
              token.output_360_date = surveySeasonInfo.output_360_date;
            }
          } else {
            surveyName = surveyCode[token.survey_code];
          }
          //Question Set Idが存在するならQuestionSet名を表示
          if (token.question_set_id && token.survey_code !== '20210') {
            let surveyInfo = survey.find(
              (item) => item._id === token.survey_id
            );
            surveyName =
              surveyName +
              ' : ' +
              surveyInfo.question_set.find(
                (item) => item._id === token.question_set_id
              ).set_name;
          }
          if (token.survey_code === '40010' || token.survey_code === '60020') {
            if (token.hero_id && token.relation !== 1) {
              surveyName = surveyName + `(${token.hero_name}様向け)`;
            }
          }
          token.surveyName = surveyName;
          token.tName = tName;
          return token;
        });

        setMySurvey(
          surveyToken.filter((item) => {
            if (!item.active) return false;
            // 実施済み(5)はシーズン範囲に関わらず常に表示（結果確認用）
            if (item.survey_status === 5) return true;
            // トライアルユーザー: シーズン期間ではなくトークンの有効期限（token_expire, JST）で制御
            if (isTrialUser) {
              if (!item.token_expire) return false;
              // 今日(JST) ≤ 有効期限(JST) … 有効期限当日まで表示（inclusive）
              return jstDateKey(new Date()) <= jstDateKey(item.token_expire);
            }
            // それ以外: SurveySeasonの回答日程範囲内のみ表示
            if (!item.survey_season_id) return false;
            const ssi = surveySeason.find(
              (s) => s._id === item.survey_season_id
            );
            if (!ssi) return false;
            return isInSurveyPeriod(ssi.survey_start_date, ssi.survey_end_date);
          })
        );
      }
    } catch (error) {
      console.log(error);
    } finally {
      // userData 未着（認証解決前）の早期 return 時はローディング表示を維持する
      if (user.userData) setIsLoading(false);
    }
  };

  useEffect(() => {
    readDB();
  }, [user]);

  const isLastSeasonComplete = (user, lastSurveySeason) => {
    let doneCnt = 0;
    let totalCnt = 0;
    const team = lastSurveySeason.relation_360.find(
      (item) => item.user_id === user._id
    );
    let seasonTokenInfo = {};
    for (let i = 0; i < team.boss.length; i++) {
      const bossId = team.boss[i];
      const bossInfo = tUsers.find((item) => item._id === bossId);
      if (bossInfo.survey_token.length === 0) {
        bossInfo.status = false;
        seasonTokenInfo = {};
      } else {
        seasonTokenInfo = bossInfo.survey_token.find(
          (tokenInfo) =>
            tokenInfo.survey_season_id === lastSurveySeason._id &&
            tokenInfo.hero_id === user._id
        );
        if (seasonTokenInfo.survey_status === 5) {
          doneCnt++;
          totalCnt++;
        } else {
          totalCnt++;
        }
      }
    }
    for (let i = 0; i < team.partner.length; i++) {
      const partnerId = team.partner[i];
      const partnerInfo = tUsers.find((item) => item._id === partnerId);
      if (partnerInfo.survey_token.length === 0) {
        partnerInfo.status = false;
        seasonTokenInfo = {};
      } else {
        seasonTokenInfo = partnerInfo.survey_token.find(
          (tokenInfo) =>
            tokenInfo.survey_season_id === lastSurveySeason._id &&
            tokenInfo.hero_id === user._id
        );
        if (seasonTokenInfo.survey_status === 5) {
          doneCnt++;
          totalCnt++;
        } else {
          totalCnt++;
        }
      }
    }
    return totalCnt === doneCnt && totalCnt > 0 ? true : false;
  };

  const makeTeam = (team) => {
    let data = { cnt: 0, done: 0 };
    team.boss.map((bossId) => {
      const bossInfo = tUsers.find((item) => item._id === bossId);
      if (bossInfo.survey_token.length === 0) {
        bossInfo.status = false;
      } else {
        let seasonTokenInfo = bossInfo.survey_token.find(
          (tokenInfo) =>
            tokenInfo.survey_season_id === team.survey_season_id &&
            tokenInfo.relation === 2 &&
            tokenInfo.hero_id === team.user_id
        );
        data.cnt += 1;
        if (seasonTokenInfo.survey_status === 5) {
          data.done += 1;
        }
      }
      return;
    });
    team.partner.map((partnerId) => {
      const partnerInfo = tUsers.find((item) => item._id === partnerId);
      if (partnerInfo.survey_token.length === 0) {
        partnerInfo.status = false;
      } else {
        let seasonTokenInfo = partnerInfo.survey_token.find(
          (tokenInfo) =>
            tokenInfo.survey_season_id === team.survey_season_id &&
            tokenInfo.relation === 3 &&
            tokenInfo.hero_id === team.user_id
        );
        data.cnt += 1;
        if (seasonTokenInfo.survey_status === 5) {
          data.done += 1;
        }
      }
      return;
    });
    team.sub.map((subId) => {
      const subInfo = tUsers.find((item) => item._id === subId);
      if (subInfo.survey_token.length === 0) {
        subInfo.status = false;
      } else {
        let seasonTokenInfo = subInfo.survey_token.find(
          (tokenInfo) =>
            tokenInfo.survey_season_id === team.survey_season_id &&
            tokenInfo.relation === 4 &&
            tokenInfo.hero_id === team.user_id
        );
        data.cnt += 1;
        if (seasonTokenInfo.survey_status === 5) {
          data.done += 1;
        }
      }
      return;
    });
    return data;
  };

  // 回答開始（MySurvey の joinSurveyHandler と同一遷移ロジック）
  const joinSurveyHandler = async (tokenInfo, e) => {
    e.preventDefault();
    let sendData = {};
    try {
      let steps = [];
      const [res, res2] = await Promise.all([
        await Axios.get(`/api/surveyToken/t/${tokenInfo._id}`),
        await Axios.get(`/api/question/${tokenInfo.survey_code}`),
      ]);
      if (res.data.success && res2.data.success) {
        const { tokenInfo } = res.data;
        const questionInfo = res2.data.question;
        if (tokenInfo.question_set_id) {
          const res3 = await Axios.get(
            `/api/questionSet/${tokenInfo.question_set_id}`
          );
          const { questionSet } = res3.data;
          let qi = [];
          questionSet.question_id.forEach((qId, index) => {
            let q = questionInfo.find((item) => item._id === qId);
            q.no = index + 1;
            if (tokenInfo.survey_code === '20110') {
              q.section = '10';
              q.reqOrigin = q.required;
            }
            qi.push(q);
          });
          sendData.questionInfo = qi;
          sendData.questionSet = questionSet;
        } else {
          sendData.questionInfo = questionInfo;
        }
        //Section情報のみ数字で取り出してSortをかける
        const secNum = [
          ...new Map(
            await sendData.questionInfo.map((info) => [
              info.section,
              Number(info.section),
            ])
          ).values(),
        ].sort((first, second) => first - second);
        for (let i = 0; i < secNum.length; i++) {
          const element = secNum[i];
          steps.push({
            section: element.toString(),
            questionInfo: sendData.questionInfo.filter(function (question) {
              return question.section === element.toString();
            }),
          });
        }
      } else {
        throw new Error('サーベイトークン情報取得に失敗しました。');
      }
      sendData.tokenInfo = tokenInfo;
      sendData.stepArr = steps;
      sendData.seasonInfo = selSeasonInfo;
      if (tokenInfo.survey_code === '90101') {
        if (ymd(tokenInfo.updatedAt) > '2025/02/01') {
          //25年3月1日以降なら新フォーマット
          props.history.push({ pathname: '/course90105', state: sendData });
        } else {
          props.history.push({ pathname: '/course90101', state: sendData });
        }
      } else if (tokenInfo.survey_code === '90301') {
        props.history.push({ pathname: '/course90301', state: sendData });
      } else if (tokenInfo.survey_code === '90501') {
        props.history.push({ pathname: '/course90501', state: sendData });
      } else if (tokenInfo.survey_code === '90502') {
        props.history.push({ pathname: '/course90502', state: sendData });
      } else {
        props.history.push({ pathname: '/newtakesurvey', state: sendData });
      }
    } catch (error) {
      alert('サーベイ参加に失敗しました。');
      console.log(error);
    }
  };

  // 結果確認（MySurvey の showConfirm と同一ロジック）
  const showConfirm = async (tokenI, type) => {
    // 簡易ログイン（メール未確認）セッションでは結果をダウンロードできない
    // （不要な PDF 生成・S3 アップロードを防ぐため、確認ダイアログを出す前にブロックする）
    if (isTrialQuickSession(user)) {
      blockResultForQuickSession({ dispatch, email: user.userData.email });
      return;
    }
    confirm({
      title: 'サーベイ結果を生成しますか？',
      content:
        'サーベイ結果を生成し別TabでPDF表示します。このままお待ちください。',
      async onOk() {
        setIsModalVisible(true);
        tokenI.type = type;
        tokenI.group_name = groupInfo.group_name;
        tokenI.company = groupInfo.group_name;
        tokenI.user_name = `${user.userData.lastname} ${user.userData.name}`;
        tokenI.emp_no = user.userData.empNo;
        let dep = '';
        if (user.userData.department) dep = user.userData.department;
        if (user.userData.department2)
          dep = dep + ' ' + user.userData.department2;
        if (user.userData.department3)
          dep = dep + ' ' + user.userData.department3;
        if (user.userData.department4)
          dep = dep + ' ' + user.userData.department4;
        if (user.userData.department5)
          dep = dep + ' ' + user.userData.department5;
        tokenI.department = dep;
        let result = {};
        if (type === 30010) {
          result = await Axios.post(
            '/api/dbModel100/makeReport30010Personal',
            tokenI
          );
        } else if (type === 1 || type === 2 || type === 3) {
          result = await Axios.post('/api/dbModel100/makeReport40010', tokenI);
        } else if (type == 10010 || type == 90101) {
          result = await Axios.post('/api/dbModel100/makeReport', tokenI);
        } else if (type == 50010 || type == 90501) {
          result = await Axios.post('/api/dbModel100/makeReport50010', tokenI);
        } else if (type == 50020 || type == 90502) {
          result = await Axios.post('/api/dbModel100/makeReport50020', tokenI);
        } else if (type == 50030 || type == 90503) {
          result = await Axios.post('/api/dbModel100/makeReport50030', tokenI);
        } else if (type == 50040) {
          //ソーシャルスタイル診断（コース版90504は未実装）
          result = await Axios.post('/api/dbModel100/makeReport50040', tokenI);
        } else if (type == 60010 || type == 90601) {
          result = await Axios.post('/api/dbModel100/makeReport60010', tokenI);
        } else if (type == 60020) {
          result = await Axios.post('/api/dbModel100/makeReport40010', tokenI);
        }
        if (result.data.success) {
          message.success({
            content: 'ファイル生成完了！',
            key: 'makecsv',
            duration: 2,
          });
          setIsModalVisible(false);
          openReport(result.data.tokenId);
        } else {
          setIsModalVisible(false);
          console.log('結果生成で問題が発生しました。');
          message.error('結果生成で問題が発生しました。');
        }
      },
      async onCancel() {},
    });
  };

  // ② 結果確認が「閲覧可能」かどうかの判定（MySurvey「結果・詳細」列の disabled 条件と同一）。
  // これが false のカードは「個別結果はありません」になるため、② 一覧から除外する。
  const isResultViewable = (r) => {
    const pubOk =
      (r.output_360_type === 1 && r.survey_status === 5) ||
      (r.output_360_type === 2 &&
        r.survey_status === 5 &&
        isPassed(r.output_360_date));
    const pubOkAll =
      (r.output_360_type === 1 && r.allStatus === 1) ||
      (r.output_360_type === 2 &&
        r.allStatus === 1 &&
        isPassed(r.output_360_date));
    if (r.survey_code === '10010' && r.output_360_type !== 0) return pubOk;
    if (r.survey_code === '50010' && r.output_360_type !== 0) return pubOk;
    if (
      r.survey_code === '90101' ||
      r.survey_code === '60010' ||
      r.survey_code === '90601' ||
      r.survey_code === '90502' ||
      r.survey_code === '90501'
    )
      return r.survey_status === 5;
    if (
      r.survey_code === '40010' &&
      r.relation === 1 &&
      r.output_360_type !== 0
    )
      return pubOkAll;
    if (r.survey_code === '30010' && r.e3_option && r.output_360_type !== 0)
      return pubOk;
    if (r.survey_code === '90301' && r.e3_option) return r.survey_status === 5;
    if (r.survey_code === '50020' && r.output_360_type !== 0) return pubOk;
    if (r.survey_code === '50030' && r.output_360_type !== 0) return pubOk;
    if (r.survey_code === '50040' && r.output_360_type !== 0) return pubOk;
    if (
      r.survey_code === '60020' &&
      r.relation === 1 &&
      r.output_360_type !== 0
    )
      return pubOkAll;
    return false;
  };

  // ① 回答する＝未回答（実施済み以外）/ ② 結果を確認する＝実施済み(5)。
  // 期限切れ・実施無効の扱い・分類境界は readDB の filter（MySurvey と同一）に従う。
  const answerList = mySurvey.filter((item) => item.survey_status !== 5);
  // ② は「結果が閲覧可能」なものだけ表示（個別結果なしカードは非表示＝ユーザー要望）。
  const resultList = mySurvey.filter(
    (item) => item.survey_status === 5 && isResultViewable(item)
  );

  // 新着のお知らせ用：最も最近に届いた（公開日が最新の）未回答サーベイ 1 件。
  const latestNewSurvey = answerList.slice().sort((a, b) => {
    const da = a.survey_start_date || a.updatedAt || '';
    const db = b.survey_start_date || b.updatedAt || '';
    return da < db ? 1 : da > db ? -1 : 0;
  })[0];

  // ② 結果確認ボタン群（MySurvey「結果・詳細」列と同一の分岐・disabled 条件・遷移）。
  const renderResultAction = (record) => {
    const cls = 'ds-btn-secondary';
    const icon = <BsBoxArrowUpRight style={{ fontSize: '16px' }} />;
    const none = (
      <Button type="default" className={cls} block disabled={true}>
        個別結果はありません
      </Button>
    );
    if (record.survey_code === '10010' && record.output_360_type !== 0) {
      return (
        <Button
          type="default"
          className={cls}
          block
          onClick={(e) => showConfirm(record, record.survey_code)}
          disabled={
            !(
              (record.output_360_type === 1 && record.survey_status === 5) ||
              (record.output_360_type === 2 &&
                record.survey_status === 5 &&
                isPassed(record.output_360_date))
            )
          }
        >
          結果確認 &nbsp; {icon}
        </Button>
      );
    } else if (record.survey_code === '50010' && record.output_360_type !== 0) {
      return (
        <Button
          type="default"
          className={cls}
          block
          onClick={(e) => showConfirm(record, record.survey_code)}
          disabled={
            !(
              (record.output_360_type === 1 && record.survey_status === 5) ||
              (record.output_360_type === 2 &&
                record.survey_status === 5 &&
                isPassed(record.output_360_date))
            )
          }
        >
          結果確認 &nbsp; {icon}
        </Button>
      );
    } else if (
      record.survey_code === '90101' ||
      record.survey_code === '60010' ||
      record.survey_code === '90601' ||
      record.survey_code === '90502' ||
      record.survey_code === '90501'
    ) {
      return (
        <Button
          type="default"
          className={cls}
          block
          onClick={(e) => showConfirm(record, record.survey_code)}
          disabled={record.survey_status === 5 ? false : true}
        >
          結果確認 &nbsp; {icon}
        </Button>
      );
    } else if (
      record.survey_code === '40010' &&
      record.relation === 1 &&
      record.output_360_type !== 0 &&
      record.type_360 !== '3x3x5' &&
      record.type_360 !== 'gates' &&
      record.type_360 !== 'initiative' &&
      record.type_360 !== 'eleader'
    ) {
      // 360診断＋本人 → 個人用 / 共有用 / 比較表（縦積みブロック）
      const enabled =
        (record.output_360_type === 1 && record.allStatus === 1) ||
        (record.output_360_type === 2 &&
          record.allStatus === 1 &&
          isPassed(record.output_360_date));
      return (
        <Space direction="vertical" style={{ width: '100%' }}>
          <Button
            type="default"
            className={cls}
            block
            onClick={(e) => showConfirm(record, 1)}
            disabled={!enabled}
          >
            個人用 &nbsp; {icon}
          </Button>
          <Button
            type="default"
            className={cls}
            block
            onClick={(e) => showConfirm(record, 2)}
            disabled={!enabled}
          >
            共有用 &nbsp; {icon}
          </Button>
          <Button
            type="default"
            className={cls}
            block
            onClick={(e) => showConfirm(record, 3)}
            disabled={
              !(
                (record.output_360_type === 1 &&
                  record.allStatus === 1 &&
                  record.lastSeason) ||
                (record.output_360_type === 2 &&
                  record.allStatus === 1 &&
                  isPassed(record.output_360_date) &&
                  record.lastSeason)
              )
            }
          >
            比較表 &nbsp; {icon}
          </Button>
        </Space>
      );
    } else if (
      record.survey_code === '40010' &&
      record.relation === 1 &&
      record.output_360_type !== 0 &&
      (record.type_360 === '3x3x5' ||
        record.type_360 === 'gates' ||
        record.type_360 === 'initiative' ||
        record.type_360 === 'eleader')
    ) {
      return (
        <Button
          type="default"
          className={cls}
          block
          onClick={(e) => showConfirm(record, 1)}
          disabled={
            !(
              (record.output_360_type === 1 && record.allStatus === 1) ||
              (record.output_360_type === 2 &&
                record.allStatus === 1 &&
                isPassed(record.output_360_date))
            )
          }
        >
          結果確認 &nbsp; {icon}
        </Button>
      );
    } else if (
      record.survey_code === '30010' &&
      record.e3_option &&
      record.output_360_type !== 0
    ) {
      return (
        <Button
          type="default"
          className={cls}
          block
          onClick={(e) => showConfirm(record, 30010)}
          disabled={
            !(
              (record.output_360_type === 1 && record.survey_status === 5) ||
              (record.output_360_type === 2 &&
                record.survey_status === 5 &&
                isPassed(record.output_360_date))
            )
          }
        >
          結果確認 &nbsp; {icon}
        </Button>
      );
    } else if (record.survey_code === '90301' && record.e3_option) {
      return (
        <Button
          type="default"
          className={cls}
          block
          onClick={(e) => showConfirm(record, 30010)}
          disabled={record.survey_status === 5 ? false : true}
        >
          結果確認 &nbsp; {icon}
        </Button>
      );
    } else if (record.survey_code === '50020' && record.output_360_type !== 0) {
      return (
        <Button
          type="default"
          className={cls}
          block
          onClick={(e) => showConfirm(record, 50020)}
          disabled={
            !(
              (record.output_360_type === 1 && record.survey_status === 5) ||
              (record.output_360_type === 2 &&
                record.survey_status === 5 &&
                isPassed(record.output_360_date))
            )
          }
        >
          結果確認 &nbsp; {icon}
        </Button>
      );
    } else if (record.survey_code === '50030' && record.output_360_type !== 0) {
      return (
        <Button
          type="default"
          className={cls}
          block
          onClick={(e) => showConfirm(record, 50030)}
          disabled={
            !(
              (record.output_360_type === 1 && record.survey_status === 5) ||
              (record.output_360_type === 2 &&
                record.survey_status === 5 &&
                isPassed(record.output_360_date))
            )
          }
        >
          結果確認 &nbsp; {icon}
        </Button>
      );
    } else if (record.survey_code === '50040' && record.output_360_type !== 0) {
      return (
        <Button
          type="default"
          className={cls}
          block
          onClick={(e) => showConfirm(record, 50040)}
          disabled={
            !(
              (record.output_360_type === 1 && record.survey_status === 5) ||
              (record.output_360_type === 2 &&
                record.survey_status === 5 &&
                isPassed(record.output_360_date))
            )
          }
        >
          結果確認 &nbsp; {icon}
        </Button>
      );
    } else if (
      record.survey_code === '60020' &&
      record.relation === 1 &&
      record.output_360_type !== 0
    ) {
      return (
        <Button
          type="default"
          className={cls}
          block
          onClick={(e) => showConfirm(record, 60020)}
          disabled={
            !(
              (record.output_360_type === 1 && record.allStatus === 1) ||
              (record.output_360_type === 2 &&
                record.allStatus === 1 &&
                isPassed(record.output_360_date))
            )
          }
        >
          結果確認 &nbsp; {icon}
        </Button>
      );
    } else {
      return none;
    }
  };

  // 未ログイン時の案内
  if (!isAuth) {
    return (
      <PageContainer>
        <div className="ds-home-empty">
          <EmptyState message="ログインすると、回答するアンケートと結果が表示されます。" />
        </div>
      </PageContainer>
    );
  }

  return (
    <Fragment>
      <PageContainer style={{ paddingTop: 'var(--space-6)' }}>
        {/* 運営からのお知らせ（メンテナンス案内 + 新着サーベイ） */}
        <section className="ds-home-section">
          <div className="ds-home-sechead">
            <div className="ds-home-sechead__bar">
              <h2 className="ds-home-sechead__title">運営からのお知らせ</h2>
            </div>
            <p className="ds-home-sechead__desc">
              メンテナンス情報や新着サーベイのご案内など、運営からの最新のお知らせをお届けします。
            </p>
          </div>

          {/* 定期メンテナンス（常時表示・固定文言） */}
          <div
            className="ds-home-notice"
            role="note"
            aria-label="定期メンテナンスのお知らせ"
          >
            <span className="ds-home-notice__icon" aria-hidden="true">
              <WarningOutlined />
            </span>
            <div className="ds-home-notice__body">
              <p className="ds-home-notice__title">
                定期メンテナンス｜03:00〜04:00
              </p>
              <p className="ds-home-notice__text">
                サーバーダウン・セッション切断の恐れがあるため、この時間帯はアンケート・診断への回答をお控えください。
              </p>
            </div>
          </div>

          {/* 新着のお知らせ（最も最近に届いた未回答サーベイ 1 件） */}
          {latestNewSurvey && (
            <Fragment>
              <h3 className="ds-home-news-subhead">新着のお知らせ</h3>
              <div className="ds-home-news-list">
                <a className="ds-home-news-row" href="/mysurvey">
                  <time className="ds-home-news-date">
                    {ymdDot(
                      latestNewSurvey.survey_start_date ||
                        latestNewSurvey.updatedAt
                    )}
                  </time>
                  <div className="ds-home-news-body">
                    <p className="ds-home-news-head">
                      <span className="ds-home-news-cat">トピック</span>
                      <span className="ds-home-news-title">
                        新しいアンケートが配信されました
                      </span>
                    </p>
                    <p className="ds-home-news-excerpt">
                      「{latestNewSurvey.surveyName}
                      」が配信されました。マイ・サーベイからご回答いただけます。
                    </p>
                  </div>
                </a>
              </div>
            </Fragment>
          )}
        </section>

        {/* ① アンケートに回答する（未回答） */}
        <section className="ds-home-section">
          <div className="ds-home-sechead">
            <div className="ds-home-sechead__bar">
              <h2 className="ds-home-sechead__title">アンケートに回答する</h2>
              <a className="ds-home-sechead__link" href="/mysurvey">
                すべて見る
                <RightOutlined />
              </a>
            </div>
            <p className="ds-home-sechead__desc">
              {/* 氏名が未設定（簡易ログイン等）の場合は「〜さん」を省略する */}
              {userName ? (
                <Fragment>
                  <span className="ds-home-username">{userName}</span>
                  さんに配信されている、未回答のアンケート・診断の一覧です。実施期限までにご回答ください。
                </Fragment>
              ) : (
                '配信されている、未回答のアンケート・診断の一覧です。実施期限までにご回答ください。'
              )}
            </p>
          </div>

          {isLoading ? (
            <div className="ds-home-loading" aria-label="サーベイ情報を読み込み中">
              <Spin size="large" />
              <p className="ds-home-loading__text">
                サーベイ情報を読み込んでいます…
              </p>
            </div>
          ) : answerList.length === 0 ? (
            <EmptyState message="回答するアンケート・診断はありません。" />
          ) : (
            <div className="ds-home-grid">
              {answerList.map((record) => (
                <article className="ds-home-card" key={record._id}>
                  <h3 className="ds-home-card__title">{record.surveyName}</h3>
                  <div className="ds-home-card__metas">
                    <div className="ds-home-card__meta">
                      <span className="ds-home-card__meta-label">実施期限</span>
                      <span className="ds-home-card__meta-val">
                        {/* トライアルユーザーはトークンの有効期限（token_expire, JST）を表示 */}
                        {isTrialUser
                          ? record.token_expire
                            ? formatJstDateOnly(record.token_expire)
                            : '-'
                          : formatSurveyDateOnly(record.survey_end_date)}
                      </span>
                    </div>
                    <div className="ds-home-card__meta">
                      <span className="ds-home-card__meta-label">
                        ステータス
                      </span>
                      <StatusBadge
                        tone={SURVEY_STATUS_TONE[record.survey_status]}
                      >
                        {record.surveyStatusName}
                      </StatusBadge>
                    </div>
                  </div>
                  <div className="ds-home-card__foot">
                    {record.survey_code === '90101' ||
                    record.survey_code === '90301' ||
                    record.survey_code === '90502' ||
                    record.survey_code === '90501' ? (
                      <Button
                        type="primary"
                        block
                        onClick={(e) => joinSurveyHandler(record, e)}
                        disabled={false}
                      >
                        回答開始
                      </Button>
                    ) : (
                      <Button
                        type="primary"
                        block
                        onClick={(e) => joinSurveyHandler(record, e)}
                        disabled={
                          record.survey_status === 0 ||
                          record.survey_status === 1
                            ? false
                            : true
                        }
                      >
                        回答開始
                      </Button>
                    )}
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>

        {/* ② 結果を確認する（実施済み） */}
        <section className="ds-home-section">
          <div className="ds-home-sechead">
            <div className="ds-home-sechead__bar">
              <h2 className="ds-home-sechead__title">結果を確認する</h2>
              <a className="ds-home-sechead__link" href="/mysurvey">
                すべて見る
                <RightOutlined />
              </a>
            </div>
            <p className="ds-home-sechead__desc">
              {/* 氏名が未設定（簡易ログイン等）の場合は「〜さん専用の」を省略する */}
              {userName ? (
                <Fragment>
                  回答済みのアンケート・診断の結果です。
                  <span className="ds-home-username">{userName}</span>
                  さん専用の個人レポートを確認できます。
                </Fragment>
              ) : (
                '回答済みのアンケート・診断の結果です。個人レポートを確認できます。'
              )}
            </p>
          </div>

          {isLoading ? (
            <div className="ds-home-loading" aria-label="サーベイ結果を読み込み中">
              <Spin size="large" />
              <p className="ds-home-loading__text">
                サーベイ情報を読み込んでいます…
              </p>
            </div>
          ) : resultList.length === 0 ? (
            <EmptyState message="確認できる結果はまだありません。" />
          ) : (
            <div className="ds-home-grid">
              {resultList.map((record) => (
                <article
                  className="ds-home-card ds-home-card--compact"
                  key={record._id}
                >
                  <h3 className="ds-home-card__title">{record.surveyName}</h3>
                  <div className="ds-home-card__metas">
                    <div className="ds-home-card__meta">
                      <span className="ds-home-card__meta-label">回答日</span>
                      <span className="ds-home-card__meta-val">
                        {record.updatedAt ? ymd(record.updatedAt) : '-'}
                      </span>
                    </div>
                  </div>
                  <div className="ds-home-card__foot">
                    {renderResultAction(record)}
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </PageContainer>

      <Modal
        title="サーベイ結果ファイル生成中..."
        open={isModalVisible}
        okButtonProps={{ disabled: true }}
        cancelButtonProps={{ disabled: true }}
        styles={{ body: { textAlign: 'center' } }}
      >
        <Spin size="large" />
      </Modal>
    </Fragment>
  );
}

export default LandingPage;
